import React from "react"
import Column from "@amzn/meridian/column"
import Text from "@amzn/meridian/text"
import { Button, Alert } from "@amzn/lcars-core-ui-components"
import BasePattern from "src/patterns/BasePattern"

interface Props { children: React.ReactNode }
interface State { hasError: boolean; error: Error | null }

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log to console so developers see it immediately in any environment
    console.error("[ErrorBoundary]", error.message, info.componentStack)
    // Fire-and-forget report to the error collection endpoint.
    // We deliberately do not await or re-throw — an error inside
    // componentDidCatch would produce an unrecoverable crash loop.
    fetch("/frontend-errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        url: window.location.href,
        ts: new Date().toISOString(),
      }),
    }).catch(() => {
      // swallow — reporting must never mask the original error
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <BasePattern title="Something went wrong">
          <Column spacing="large">
            <Alert
              type="error"
              primaryText={this.state.error?.message || "An unexpected error occurred."}
            />
            <Text type="b200">Try refreshing the page.</Text>
            <Button onInteraction={() => window.location.reload()}>Refresh</Button>
          </Column>
        </BasePattern>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
